import { marked, Renderer } from 'marked'
import DOMPurify from 'dompurify'

const renderer = new Renderer()
renderer.link = ({ href, text }) => {
  return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
}

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  // https?/mailto for absolute links, "#" for same-page fragments, and a
  // single leading "/" (not "//") for relative paths -- "//host" is protocol-
  // relative and would otherwise resolve to an arbitrary external origin
  // while looking like an internal link. Matches what Redcarpet's own
  // safe_links_only already allows on the backend renderer (it separately
  // requires the character after "/" to be alphanumeric, which also excludes "//").
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):|^#|^\/(?!\/)/i,
  // Without this, DOMPurify also tests target/rel's values against ALLOWED_URI_REGEXP
  // (its default safe-attribute list only exempts things like class/id/title), so a
  // strict scheme regexp silently strips target="_blank" and rel="noopener noreferrer".
  ADD_URI_SAFE_ATTR: ['target', 'rel'],
}

const sanitizeHtml = (html: string) => DOMPurify.sanitize(html, SANITIZE_CONFIG)

/**
 * Parses markdown text into HTML with special handling for links and line breaks.
 *
 * Features:
 * - Converts URLs (both plain text and markdown links) to anchor tags that open in new tabs
 * - Converts line breaks (\n) to HTML <br> tags
 * - Returns empty string if input is falsy
 *
 * @example
 * // Plain URL
 * parseMarkdown("Visit https://example.com")
 * // Returns: Visit <a href="https://example.com" target="_blank" rel="noopener noreferrer">https://example.com</a>
 *
 * @example
 * // Markdown link
 * parseMarkdown("Visit [Example](https://example.com)")
 * // Returns: Visit <a href="https://example.com" target="_blank" rel="noopener noreferrer">Example</a>
 *
 * @example
 * // Line breaks
 * parseMarkdown("Line 1\nLine 2")
 * // Returns: Line 1<br>Line 2
 *
 * @param {string} markdown - The markdown text to parse
 * @returns {string} The parsed HTML string
 */
export const parseMarkdown = (markdown: string) => {
  return sanitizeHtml(marked(markdown.replace(/\n/g, '<br/>'), { renderer }) as string)
}

/**
 * Parses markdown text into sanitized HTML without the line-break preprocessing of `parseMarkdown`.
 *
 * @param {string} markdown - The markdown text to parse
 * @returns {string} The parsed, sanitized HTML string
 */
export const parseMarkdownV2 = (markdown: string) => {
  return sanitizeHtml(marked(markdown, { renderer }) as string)
}
